import type { PromotionPrerequisiteIssue } from '../../../services/BaseBackendService';
import {
  blockingPromotionIssues,
  isPromotionPrerequisitesError,
  overridableFundStatusIssues,
  structuredApiErrorMessage,
} from '../investorPromotionPrerequisites';
import { promotionIssueDisplayText } from './promotionPrerequisiteMessages';

export interface PromotionPrerequisiteUiState {
  investmentPromptModalOpen: boolean;
  investorActionInProgress?: string;
  actionTypeInProgress?: 'delete' | 'promote' | 'demote';
  investmentPromptInProgress: boolean;
}

export interface PromotionPrerequisiteUiPatch extends Partial<PromotionPrerequisiteUiState> {
  promotionPrerequisiteModalOpen?: boolean;
  promotionPrerequisiteIssues?: PromotionPrerequisiteIssue[];
  promotionPrerequisiteRetry?: () => Promise<void>;
  promotionPrerequisiteOnSuccess?: () => void;
  promotionPrerequisiteConfirmInProgress?: boolean;
  investmentPromptError?: string;
}

export type SetPromotionPrerequisiteState = (
  update:
    | PromotionPrerequisiteUiPatch
    | ((prev: PromotionPrerequisiteUiState) => PromotionPrerequisiteUiPatch),
) => void;

export function runWithPromotionPrerequisites(
  run: (opts: { overrideFundStatusRestriction?: boolean }) => Promise<void>,
  onSuccess: () => void,
  getUiState: () => PromotionPrerequisiteUiState,
  setUiState: SetPromotionPrerequisiteState,
  relaxUiWhileAwaitingAck = false,
): void {
  run({})
    .then(() => {
      onSuccess();
    })
    .catch((err: unknown) => {
      if (isPromotionPrerequisitesError(err)) {
        const blocking = blockingPromotionIssues(err.issues);
        if (blocking.length > 0) {
          const blockingText = blocking.map((i) => promotionIssueDisplayText(i)).join(' ');
          const promptOpen = getUiState().investmentPromptModalOpen;
          setUiState({
            investorActionInProgress: undefined,
            actionTypeInProgress: undefined,
            investmentPromptInProgress: false,
            ...(promptOpen ? { investmentPromptError: blockingText } : {}),
          });
          if (!promptOpen) {
            alert(blockingText);
          }
          return;
        }

        const overridable = overridableFundStatusIssues(err.issues);
        if (overridable.length > 0) {
          setUiState((prev) => ({
            promotionPrerequisiteModalOpen: true,
            promotionPrerequisiteIssues: overridable,
            promotionPrerequisiteRetry: () => run({ overrideFundStatusRestriction: true }),
            promotionPrerequisiteOnSuccess: onSuccess,
            investorActionInProgress: relaxUiWhileAwaitingAck
              ? undefined
              : prev.investorActionInProgress,
            actionTypeInProgress: relaxUiWhileAwaitingAck ? undefined : prev.actionTypeInProgress,
            investmentPromptInProgress: relaxUiWhileAwaitingAck
              ? false
              : prev.investmentPromptInProgress,
          }));
          return;
        }
      }

      console.error('Investor operation failed:', err);
      const msg = structuredApiErrorMessage(err);
      const promptOpen = getUiState().investmentPromptModalOpen;
      setUiState({
        investorActionInProgress: undefined,
        actionTypeInProgress: undefined,
        investmentPromptInProgress: false,
        ...(promptOpen ? { investmentPromptError: msg } : {}),
      });
      if (!promptOpen) {
        alert(msg);
      }
    });
}

export async function confirmPromotionPrerequisiteRetry(
  retry: (() => Promise<void>) | undefined,
  onSuccess: (() => void) | undefined,
  getUiState: () => Pick<PromotionPrerequisiteUiState, 'investmentPromptModalOpen'>,
  setUiState: SetPromotionPrerequisiteState,
): Promise<void> {
  if (!retry) {
    return;
  }

  setUiState({ promotionPrerequisiteConfirmInProgress: true });

  try {
    await retry();
    const success = onSuccess;
    setUiState({
      promotionPrerequisiteModalOpen: false,
      promotionPrerequisiteIssues: [],
      promotionPrerequisiteRetry: undefined,
      promotionPrerequisiteOnSuccess: undefined,
      promotionPrerequisiteConfirmInProgress: false,
    });
    success?.();
  } catch (err: unknown) {
    console.error('Promotion prerequisite retry failed:', err);
    const msg = structuredApiErrorMessage(err);
    const promptOpen = getUiState().investmentPromptModalOpen;
    setUiState({
      promotionPrerequisiteConfirmInProgress: false,
      ...(promptOpen ? { investmentPromptError: msg } : {}),
    });
    if (!promptOpen) {
      alert(msg);
    }
  }
}
